import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft,
  FaHistory,
  FaSearch,
  FaUserCircle,
  FaSignOutAlt,
  FaBell,
  FaServer,
  FaCode,
  FaChartBar,
  FaDatabase,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaBars
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const CloudStaffActivityLog = () => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 25;

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadProfile();
  }, [navigate]);

  useEffect(() => {
    loadLogs();
  }, [page]);

  const loadProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserName(`${data.firstName || data.first_name || ''} ${data.lastName || data.last_name || ''}`);
      }
    } catch (e) {
      console.error('Error loading profile:', e);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const offset = page * limit;
      const res = await fetch(
        getApiEndpoint(`/api/cloud-staff/activity-log?limit=${limit}&offset=${offset}`),
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error('Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / limit);

  const filteredLogs = searchTerm
    ? logs.filter(log =>
        (log.property_name && log.property_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.asset_name && log.asset_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.safety_module && log.safety_module.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (log.new_value && log.new_value.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : logs;

  return (
    <div className="cs-dashboard">
      {/* Header */}
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge">
            <FaServer />
            <span>Cloud Staff Portal</span>
          </div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn" title="Notifications">
              <FaBell />
            </button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header">
                    <FaUserCircle className="cs-menu-avatar" />
                    <div>
                      <p className="cs-menu-name">{userName || 'Cloud Staff'}</p>
                      <p className="cs-menu-role">Cloud Staff Admin</p>
                    </div>
                  </div>
                  <div className="cs-user-menu-divider" />
                  <Link to="/cloud-staff/dashboard" className="cs-menu-item">
                    <FaChartBar /> Dashboard
                  </Link>
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout">
                    <FaSignOutAlt /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar active="activity-log" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />

        {/* Content */}
        <main className="cs-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #21262d' }}>
            <Link to="/cloud-staff/dashboard" style={{
              width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid #30363d',
              color: '#8b949e', textDecoration: 'none', transition: 'all 0.2s'
            }}>
              <FaArrowLeft />
            </Link>
            <div>
              <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0f6fc', margin: 0 }}>Activity Log</h1>
              <p style={{ fontSize: '0.85rem', color: '#8b949e', margin: 0 }}>All asset code changes made by you</p>
            </div>
          </div>

          {/* Toolbar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem'
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              background: '#0d1117', border: '1px solid #30363d', borderRadius: '8px', padding: '0 0.75rem'
            }}>
              <FaSearch style={{ color: '#484f58', fontSize: '0.85rem' }} />
              <input
                type="text"
                placeholder="Search activity..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  background: 'none', border: 'none', color: '#e1e4e8', fontSize: '0.8rem',
                  padding: '0.55rem 0', outline: 'none', width: '200px'
                }}
              />
            </div>
            <div style={{ fontSize: '0.8rem', color: '#8b949e' }}>
              {total} total entries
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="cs-loading">
              <div className="cs-spinner" />
              <p>Loading activity log...</p>
            </div>
          ) : filteredLogs.length > 0 ? (
            <>
              <div className="cs-activity-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date/Time</th>
                      <th>Building</th>
                      <th>Module</th>
                      <th>Asset Type</th>
                      <th>Asset</th>
                      <th>Field Changed</th>
                      <th>Old Value</th>
                      <th>New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map(log => (
                      <tr key={log.id}>
                        <td>{formatDate(log.created_at)}</td>
                        <td>{log.property_name || `#${log.property_id}`}</td>
                        <td><span className="cs-badge module">{log.safety_module}</span></td>
                        <td><span className="cs-badge module">{log.asset_type}</span></td>
                        <td>{log.asset_name || `#${log.asset_id}`}</td>
                        <td style={{ fontFamily: "'SF Mono', Monaco, monospace", fontSize: '0.75rem' }}>{log.field_changed}</td>
                        <td className="cs-old-value">{log.old_value || '(empty)'}</td>
                        <td className="cs-new-value">{log.new_value || '(empty)'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '1rem', padding: '1.5rem 0'
                }}>
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.35rem',
                      padding: '0.5rem 0.85rem', background: 'rgba(255,255,255,0.06)',
                      border: '1px solid #30363d', borderRadius: '8px', color: '#8b949e',
                      cursor: page === 0 ? 'not-allowed' : 'pointer', fontSize: '0.8rem',
                      opacity: page === 0 ? 0.4 : 1
                    }}
                  >
                    <FaChevronLeft /> Previous
                  </button>
                  <span style={{ fontSize: '0.8rem', color: '#8b949e' }}>
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.35rem',
                      padding: '0.5rem 0.85rem', background: 'rgba(255,255,255,0.06)',
                      border: '1px solid #30363d', borderRadius: '8px', color: '#8b949e',
                      cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', fontSize: '0.8rem',
                      opacity: page >= totalPages - 1 ? 0.4 : 1
                    }}
                  >
                    Next <FaChevronRight />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="cs-empty-state">
              <FaDatabase />
              <p>No activity log entries found</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CloudStaffActivityLog;
